<script lang="ts" setup>
import { computed, inject, ref } from 'vue';

import { accountService } from '@exchange/libs/account/service/src';
import { authService } from '@exchange/libs/auth/service/src';
import { subaccountsService } from '@exchange/libs/trading-accounts/service/src';
import { currencyService } from '@exchange/libs/utils/currency/src';

import OrderContainer from './OrderContainer.vue';
import OrderFormSideSwitcher from './OrderFormSideSwitcher.vue';

defineProps({
  marketId: { type: String, required: true },
});

const balanceMasked = inject('balanceMasked', ref(false));

const { accountId } = accountService;
const { defaultFiatCurrency, currencies, fiatPrecision } = currencyService;
const { list: accounts } = subaccountsService;

const panel = {
  value: ref(false),
  open: () => {
    panel.value.value = true;
  },
};

const userHasBeenAuthenticated = computed(() => authService.hasBeenAuthenticated);
const activeAccount = computed(() => accounts.value.find((account) => account.accountId === accountId.value));

const onActiveSideChange = () => {
  panel.open();
};
</script>

<template>
  <div class="order-buy-sell-panel">
    <div class="order-buy-sell-panel__container">
      <order-form-side-switcher
        class="order-buy-sell-panel__side-switcher"
        :on-active-side-change="onActiveSideChange"
      />
      <ot-panel
        v-model:open="panel.value.value"
        :open-expended="true"
      >
        <div class="order-buy-sell-panel__panel">
          <div
            v-if="userHasBeenAuthenticated"
            class="order-buy-sell-panel__active-account"
          >
            <span class="order-buy-sell-panel__account-name">{{ activeAccount && activeAccount.name }}:</span>
            <ot-balance
              class="order-buy-sell-panel__account-balance"
              :value="activeAccount && activeAccount.getBalanceTotal(currencies).all"
              :precision="fiatPrecision"
              :symbol="defaultFiatCurrency"
              :masked="balanceMasked"
              :light-symbol="true"
            />
          </div>
          <order-container
            :market-id="marketId"
            :transparent-background="true"
          />
        </div>
      </ot-panel>
    </div>
  </div>
</template>

<style lang="scss">
.order-buy-sell-panel {
  height: var(--app-mobile-buy-sell-panel-trigger-height);

  &__container {
    position: fixed;
    z-index: var(--panel-container-z-index);
    bottom: 0;
    display: flex;
    width: 100%;
    height: var(--app-mobile-buy-sell-panel-trigger-height);
    align-items: center;
    padding: 0 16px;
    border-top: 3px solid rgb(var(--v-theme-elevation-0));
    background-color: rgb(var(--v-theme-elevation-1));
  }

  &__side-switcher {
    flex: 1 0 auto;
  }

  &__panel {
    padding: 0 0 16px;
  }

  &__active-account {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__account-name {
    margin-right: 4px;
    color: rgb(var(--v-theme-text-primary));
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
  }

  &__account-balance {
    display: inline-flex;
    color: rgb(var(--v-theme-text-primary));
    font-size: var(--font-size-sm);
  }
}
</style>
